import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import Reveal from "../components/Reveal";
import api from "../lib/api";
import { useSite } from "../context/SiteContext";

export default function Pricing() {
    const [plans, setPlans] = useState([]);
    const { openPaymentSoon } = useSite();
    const navigate = useNavigate();
    useEffect(() => { api.get("/pricing").then((r) => setPlans(r.data)).catch(() => {}); }, []);

    const choose = async (plan) => {
        if (plan.price === 0) { navigate("/about"); return; }
        openPaymentSoon({ title: plan.name, slug: plan.slug });
    };

    return (
        <div data-testid="pricing-page">
            <section className="bg-white py-20 lg:py-24 border-b border-neutral-200">
                <div className="mx-container text-center max-w-2xl mx-auto">
                    <p className="overline mb-5">Pricing</p>
                    <h1 className="font-display font-black uppercase tracking-tightest text-5xl sm:text-6xl leading-[0.92]">Invest in your <span className="text-brand-yellow">career.</span></h1>
                    <p className="mt-6 text-neutral-500 text-lg">Transparent packages for every stage. No hidden fees, just a clear path to your next release.</p>
                </div>
            </section>

            <section className="py-16 lg:py-24 bg-offwhite">
                <div className="mx-container grid md:grid-cols-3 gap-6 items-stretch">
                    {plans.length === 0 && [0, 1, 2].map((i) => (
                        <div key={`sk-${i}`} className="h-full flex flex-col p-8 bg-white border border-neutral-200 animate-pulse" data-testid={`pricing-skeleton-${i}`}>
                            <div className="h-7 w-1/2 bg-neutral-200" />
                            <div className="mt-3 h-4 w-3/4 bg-neutral-100" />
                            <div className="mt-6 h-10 w-2/3 bg-neutral-200" />
                            <div className="mt-8 space-y-3 flex-1">
                                {[0, 1, 2, 3, 4].map((k) => <div key={k} className="h-4 w-full bg-neutral-100" />)}
                            </div>
                            <div className="mt-8 h-12 w-full bg-neutral-200" />
                        </div>
                    ))}
                    {plans.map((p, i) => (
                        <Reveal key={p.slug} delay={i * 0.08} className="h-full">
                            <div className={`h-full flex flex-col items-center text-center p-8 ${p.featured ? "bg-navy text-white grain" : "bg-white border border-neutral-200"}`} data-testid={`pricing-plan-${p.slug}`}>
                                {p.featured && <span className="bg-brand-yellow text-ink text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 mb-4">Most popular</span>}
                                <h3 className="font-display font-bold text-2xl">{p.name}</h3>
                                <p className={`mt-1 text-sm ${p.featured ? "text-white/60" : "text-neutral-500"}`}>{p.tagline}</p>
                                <div className="mt-6">
                                    {p.price === 0 ? (
                                        <span className="font-display font-black text-4xl">Custom</span>
                                    ) : (
                                        <><span className="font-display font-black text-4xl">₹{p.price.toLocaleString("en-IN")}</span><span className={`text-sm ${p.featured ? "text-white/50" : "text-neutral-400"}`}> {p.period}</span></>
                                    )}
                                </div>
                                <ul className="mt-7 space-y-3 flex-1">
                                    {p.features.map((f) => (
                                        <li key={f} className="flex items-start justify-center gap-2.5 text-sm">
                                            <Check className={`h-4 w-4 shrink-0 mt-0.5 ${p.featured ? "text-brand-yellow" : "text-brand-blue"}`} />
                                            <span className={p.featured ? "text-white/80" : "text-neutral-700"}>{f}</span>
                                        </li>
                                    ))}
                                </ul>
                                <button onClick={() => choose(p)} className={`mt-8 w-full ${p.featured ? "btn-primary" : "btn-dark"}`} data-testid={`pricing-cta-${p.slug}`}>
                                    {p.cta} <ArrowRight className="h-4 w-4" />
                                </button>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>
        </div>
    );
}
